/*
 * RBN limited drop schedule: the homepage campaign (sections/rbn-limited-drop.liquid), the event page
 * (sections/rbn-event-page.liquid) and the "Limited drops" menu link (snippets/rbn-event-nav-link.liquid).
 * Turns the start and end (date, time, IANA time zone) into exact moments. Before the start they stay hidden; at the
 * start they appear (the campaign and event page count down); at the end they hide again and their links are removed.
 * The page does not need a reload for either change. Display only: it does not stop sales.
 * Limited Drops hub cards (<rbn-drop-card>, sections/rbn-drop-hub): COMING SOON before the start, LIVE NOW during,
 * removed at the end; when the last card goes, visitors are sent on to Shop All.
 * Testing in a theme preview (never on the live theme): add ?rbn_drop_now=2026-10-31T12:00:00Z to the URL to
 * pretend the current time is that moment.
 */
(() => {
  if (customElements.get('rbn-limited-drop')) return;

  /** Offset of a time zone from UTC, in milliseconds, at the instant `ms`. */
  function zoneOffset(ms, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(new Date(ms));
    const v = {};
    for (const p of parts) if (p.type !== 'literal') v[p.type] = Number(p.value);
    const asUtc = Date.UTC(v.year, v.month - 1, v.day, v.hour === 24 ? 0 : v.hour, v.minute, v.second);
    return asUtc - Math.floor(ms / 1000) * 1000;
  }

  /** The UTC instant of a wall-clock date and time in a time zone, or NaN when the settings are not valid. */
  function momentFor(date, time, timeZone) {
    const d = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec((date || '').trim());
    const t = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec((time || '').trim());
    if (!d || !t) return NaN;
    const wall = Date.UTC(+d[1], +d[2] - 1, +d[3], +t[1], +t[2], +(t[3] || 0));
    try {
      let guess = wall - zoneOffset(wall, timeZone);
      guess = wall - zoneOffset(guess, timeZone); // second pass settles daylight-saving changes
      return guess;
    } catch (e) {
      return NaN; // unknown time zone
    }
  }

  /** Test clock from ?rbn_drop_now=, honoured only outside the live theme. */
  function testNow() {
    try {
      const role = window.Shopify && window.Shopify.theme && window.Shopify.theme.role;
      if (role === 'main' && !(window.Shopify && window.Shopify.designMode)) return 0;
      const raw = new URLSearchParams(window.location.search).get('rbn_drop_now');
      if (!raw) return 0;
      const ms = Date.parse(raw);
      return Number.isNaN(ms) ? 0 : ms;
    } catch (e) {
      return 0;
    }
  }

  const pad = (n) => String(n).padStart(2, '0');
  const MAX_WAIT = 6 * 60 * 60 * 1000; // re-check at least every 6 hours (timers cannot wait for weeks)

  class RbnLimitedDrop extends HTMLElement {
    connectedCallback() {
      this.tz = this.dataset.timezone || 'America/New_York';
      this.start = momentFor(this.dataset.startDate, this.dataset.startTime, this.tz);
      this.end = momentFor(this.dataset.endDate, this.dataset.endTime, this.tz);
      this.editor = this.hasAttribute('data-editor');
      // Event page: the "not live" message shown instead of the event outside its window.
      this.offEl = this.dataset.offTarget ? document.getElementById(this.dataset.offTarget) : null;
      const fake = testNow();
      this.skew = fake ? fake - Date.now() : 0;
      this.els = {
        days: this.querySelector('[data-drop-days]'),
        hours: this.querySelector('[data-drop-hours]'),
        minutes: this.querySelector('[data-drop-minutes]'),
        seconds: this.querySelector('[data-drop-seconds]'),
        status: this.querySelector('[data-drop-status]'),
      };
      const ends = this.querySelector('[data-drop-ends]');
      if (ends && !Number.isNaN(this.end)) ends.textContent = 'Ends ' + this.format(this.end);
      this.tick();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
    }

    now() {
      return Date.now() + this.skew;
    }

    format(ms) {
      try {
        return new Intl.DateTimeFormat('en-US', {
          timeZone: this.tz,
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          timeZoneName: 'short',
        }).format(new Date(ms));
      } catch (e) {
        return new Date(ms).toISOString();
      }
    }

    /** 'invalid' | 'before' | 'live' | 'after' */
    phase(now) {
      if (Number.isNaN(this.start) || Number.isNaN(this.end) || this.end <= this.start) return 'invalid';
      if (now < this.start) return 'before';
      if (now < this.end) return 'live';
      return 'after';
    }

    tick = () => {
      clearTimeout(this.timer);
      const now = this.now();
      const phase = this.phase(now);

      if (this.editor) {
        // Theme editor: always visible so it can be edited; say what visitors see right now.
        const s = this.els.status;
        if (s) {
          s.textContent =
            phase === 'before' ? `Scheduled: hidden from visitors until ${this.format(this.start)}.`
            : phase === 'live' ? `Live now: visible to visitors until ${this.format(this.end)}.`
            : phase === 'after' ? 'Ended: hidden from visitors. Set new dates for the next campaign.'
            : 'Dates not valid: hidden from visitors.';
        }
      } else if (phase === 'live') {
        this.hidden = false;
        if (this.offEl) this.offEl.hidden = true;
      } else {
        this.hidden = true;
        if (this.offEl) this.offEl.hidden = false;
        if (phase === 'after' || phase === 'invalid') {
          // Over: take the offer out of the page, not just out of sight. On the event page the whole event goes
          // (heading, artwork, countdown, products, buttons) and only the "not live" message stays.
          if (this.offEl) {
            this.remove();
            return;
          }
          this.querySelectorAll('a').forEach((a) => a.remove());
          return;
        }
      }

      if (phase === 'before') {
        this.timer = setTimeout(this.tick, Math.min(this.start - now + 20, MAX_WAIT));
        return;
      }
      if (phase !== 'live') return;

      const left = this.end - now;
      const s = Math.floor(left / 1000);
      const { days, hours, minutes, seconds } = this.els;
      if (days) days.textContent = pad(Math.floor(s / 86400));
      if (hours) hours.textContent = pad(Math.floor((s % 86400) / 3600));
      if (minutes) minutes.textContent = pad(Math.floor((s % 3600) / 60));
      if (seconds) seconds.textContent = pad(s % 60);
      // Wake just after the next whole second; a sleeping background tab catches up on the next tick.
      this.timer = setTimeout(this.tick, (left % 1000) + 20);
    };
  }

  customElements.define('rbn-limited-drop', RbnLimitedDrop);

  /* The existing limited-drop controller also runs the seasonal Limited Drops hub. */
  class RbnSeasonalHub extends HTMLElement {
    connectedCallback() {
      this.cards = [...this.querySelectorAll('[data-seasonal-item]')].map(el => ({
        el, start:momentFor(el.dataset.startDate,el.dataset.startTime,el.dataset.timezone),
        end:momentFor(el.dataset.endDate,el.dataset.endTime,el.dataset.timezone),
        enabled:el.dataset.enabled==='true'
      }));
      const fake = testNow();
      this.skew = fake ? fake - Date.now() : 0;
      this.empty = this.querySelector('[data-seasonal-empty]');
      this.render();
    }
    disconnectedCallback() { clearTimeout(this.timer); }
    render = () => {
      clearTimeout(this.timer);
      const now = Date.now()+this.skew;
      const valid = this.cards.filter(x => x.enabled && Number.isFinite(x.start) && Number.isFinite(x.end) && x.end>x.start).sort((a,b)=>a.start-b.start);
      const live = valid.filter(x=>now>=x.start && now<x.end);
      const future = valid.filter(x=>now<x.start);
      const ended = valid.filter(x=>now>=x.end);
      const primary = live.at(-1) || future[0] || ended.at(-1) || null;
      const teaser = null;
      for (const x of this.cards) {
        const show = x.enabled && Number.isFinite(x.start) && Number.isFinite(x.end) && x.end>x.start && (this.hasAttribute('data-keep-ended') || now<x.end || x===primary);
        x.el.hidden = !show;
        if (!show) continue;
        const phase = now<x.start ? 'soon' : now<x.end ? 'live' : 'ended';
        const ready = x.el.dataset.ready==='true';
        x.el.dataset.phase=phase;
        const status=x.el.querySelector('[data-seasonal-status]');
        if(status)status.textContent=phase==='soon'?'COMING SOON':phase==='ended'?'ENDED':ready?'LIVE NOW':'SETUP REQUIRED';
        const timing=x.el.querySelector('[data-seasonal-timing]');
        if(timing)timing.textContent=phase==='soon'?'Opens '+x.el.dataset.startDate+' '+x.el.dataset.startTime+' ('+x.el.dataset.timezone+')':phase==='live'?'Ends '+x.el.dataset.endDate+' '+x.el.dataset.endTime+' ('+x.el.dataset.timezone+')':'This event has ended.';
        const countdown=x.el.querySelector('[data-seasonal-countdown]');
        if(countdown) {
          countdown.hidden=phase==='ended';
          if(phase!=='ended') {
            const secs=Math.max(0,Math.floor(((phase==='soon'?x.start:x.end)-now)/1000));
            [Math.floor(secs/86400),Math.floor((secs%86400)/3600),Math.floor((secs%3600)/60),secs%60].forEach((num,i)=>{countdown.children[i].textContent=pad(num);});
          }
        }
        const buy=x.el.querySelector('[data-seasonal-go]');
        if(buy)buy.hidden=phase!=='live'||!ready;
        const note=x.el.querySelector('[data-seasonal-setup]');
        if(note)note.hidden=phase==='ended';

      }

      if(this.empty)this.empty.hidden=Boolean(primary);
      this.timer=setTimeout(this.render,1000);
    };
  }
  customElements.define('rbn-seasonal-hub',RbnSeasonalHub);

  /* Menu links ([data-rbn-window]): shown only inside their window, re-checked at the next start or end. */
  let scanTimer = 0;
  function scanWindows() {
    clearTimeout(scanTimer);
    const fake = testNow();
    const now = fake || Date.now();
    let wait = Infinity;
    const lists = new Set();
    for (const el of document.querySelectorAll('[data-rbn-window]')) {
      const tz = el.dataset.timezone || 'America/New_York';
      const start = momentFor(el.dataset.startDate, el.dataset.startTime, tz);
      const end = momentFor(el.dataset.endDate, el.dataset.endTime, tz);
      const valid = !Number.isNaN(start) && !Number.isNaN(end) && end > start;
      const live = valid && now >= start && now < end;
      if (el.hidden === live) {
        el.hidden = !live;
        const list = el.closest('overflow-list');
        if (list) lists.add(list);
      }
      if (!valid || now >= end) {
        el.remove(); // over: no dead link stays in the page
        continue;
      }
      wait = Math.min(wait, (now < start ? start : end) - now);
    }
    // The desktop menu moves items that do not fit into "More"; let it measure again.
    for (const list of lists) list.dispatchEvent(new CustomEvent('reflow', { detail: {} }));
    if (wait !== Infinity && !fake) scanTimer = setTimeout(scanWindows, Math.min(wait + 20, MAX_WAIT));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scanWindows, { once: true });
  else scanWindows();
})();

